/**
 * /pipeline-facility/rates/new and /pipeline-facility/rates/:id — one rate on
 * the PF rate card. Full pages, no dialogs; the delete is confirmed inline.
 *
 * Deleting a rate leaves every ticket line already priced from it alone (the
 * line keeps its copied rate, code and name) — the confirm says so.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { usePfCustomers, usePfEquipmentOptions, usePfMeta } from '../../../hooks/usePipelineFacility';
import { useCreatePfRate, useDeletePfRate, usePfEquipmentTypes, usePfRate, usePfRates, useUpdatePfRate } from '../../../hooks/usePfTickets';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canWritePfTickets, pfTicketRefusal } from '../../../lib/pf-tickets';
import { catalogLabel, pfDay } from '../../../lib/pipeline-facility';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, PF_EYEBROW, PfEmpty, PfLoading, PfPanel, PfSpec } from '../PfBits';
import { PfInlineConfirm } from '../digs/DigBits';
import { PfRateForm, type PfRateFormOptions } from './RateForm';
import { pfRateAppliesTo } from './RatesScreen';
import { pfRateText } from './TicketBits';

function useRateFormOptions(enabled: boolean): PfRateFormOptions {
  const customers = usePfCustomers(enabled).data ?? [];
  const equipment = usePfEquipmentOptions(enabled).data ?? [];
  const equipmentTypes = usePfEquipmentTypes(enabled).data ?? [];
  return { customers, equipment, equipmentTypes };
}

export function PfNewRateScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/pipeline-facility/rates');
  const catalog = usePfMeta().data?.data ?? null;
  const metaQuery = usePfRates();
  const options = useRateFormOptions(true);
  const create = useCreatePfRate();
  const [error, setError] = useState<string | null>(null);
  const meta = metaQuery.data?.meta ?? null;
  return (
    <div data-testid="pf-rate-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={PF_EYEBROW} title="New rate" description="One line on the rate card." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <PfPanel eyebrow="Rate card" title="New rate">
            <PfRateForm
              catalog={catalog}
              options={options}
              currency={meta?.currency ?? null}
              today={meta?.today ?? null}
              defaultCustomerId={searchParams.get('customerId') ?? ''}
              pending={create.isPending}
              error={error}
              submitLabel="Add rate"
              onCancel={back}
              onSubmit={(body) => {
                setError(null);
                create.mutate(body, {
                  onSuccess: (r) => {
                    toast.success('Rate added');
                    navigate(r.data ? `/pipeline-facility/rates/${r.data.id}` : '/pipeline-facility/rates', { replace: true });
                  },
                  onError: (e) => setError(pfTicketRefusal(e, 'The rate could not be added.')),
                });
              }}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}

export default function PfRateDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/rates');
  const { user } = useAuth();
  const canWrite = canWritePfTickets(user?.role);
  const query = usePfRate(id);
  const catalog = usePfMeta().data?.data ?? null;
  const metaQuery = usePfRates(undefined, canWrite);
  const update = useUpdatePfRate();
  const del = useDeletePfRate();
  const [editing, setEditing] = useState(false);
  const options = useRateFormOptions(canWrite && editing);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const r = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="pf-rate-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title={r?.code || (query.isLoading ? 'Rate' : '—')}
        description={r ? `${r.name} · ${r.customerId ? r.customerName ?? '—' : 'Default card'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          r && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading rate" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !r) ? (
            <PfPanel eyebrow="Rate card" title="Rate">
              <PfEmpty>This rate was not found. It may have been deleted.</PfEmpty>
            </PfPanel>
          ) : failure || !r ? (
            <PfPanel eyebrow="Rate card" title="Rate">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this rate" />
            </PfPanel>
          ) : (
            <>
              {deleting && (
                <PfInlineConfirm
                  testId="pf-rate-delete-confirm"
                  message={`Delete ${r.code} from the rate card? Ticket lines already priced from it keep their price.`}
                  confirmLabel="Delete rate"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(r.id, {
                      onSuccess: () => {
                        toast.success('Rate deleted');
                        navigate('/pipeline-facility/rates', { replace: true });
                      },
                      onError: (e) => setDeleteError(pfTicketRefusal(e, 'The rate could not be deleted.')),
                    })
                  }
                />
              )}
              {editing && canWrite ? (
                <PfPanel eyebrow="Edit" title={`Edit ${r.code}`}>
                  <PfRateForm
                    rate={r}
                    catalog={catalog}
                    options={options}
                    currency={r.currency}
                    today={metaQuery.data?.meta.today ?? null}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save rate"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      update.mutate(
                        { id: r.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Rate saved');
                            setEditing(false);
                          },
                          onError: (e) => setEditError(pfTicketRefusal(e, 'The rate could not be saved.')),
                        },
                      );
                    }}
                  />
                </PfPanel>
              ) : (
                <PfPanel eyebrow="Rate card" title="Details" testId="pf-rate-details">
                  <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                    <PfSpec label="Code" value={r.code || '—'} mono />
                    <PfSpec label="Name" value={r.name || '—'} />
                    <PfSpec label="Card" value={r.customerId ? r.customerName || '—' : 'Default card'} />
                    <PfSpec label="Kind" value={r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} />
                    <PfSpec label="Rate" value={pfRateText(r.rate, r.currency, r.unit, r.distanceUnit)} mono />
                    <PfSpec label="Unit" value={r.unitLabel || catalogLabel(catalog?.priceUnits, r.unit)} />
                    <PfSpec label="Currency" value={r.currency || '—'} />
                    <PfSpec label="Applies to" value={pfRateAppliesTo(r, catalog?.crewRoles)} />
                    <PfSpec label="Starts" value={pfDay(r.effectiveFrom)} />
                    <PfSpec label="Ends" value={r.effectiveTo ? pfDay(r.effectiveTo) : 'No end'} />
                  </dl>
                  {(r.description || r.notes) && (
                    <div className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                      {r.description && <p>{r.description}</p>}
                      {r.notes && <p className="text-fieldiq-muted">{r.notes}</p>}
                    </div>
                  )}
                </PfPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
